import {
  CustomAnalyticsEvent,
  Domain,
  DynamicPage,
  ExternalPixelEvent,
} from '@superfunnel/types';
import {
  camelCaseToTitle,
  capitalize,
  chunk,
  distinct,
  groupBy,
} from '@superfunnel/utils';
import {
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { RefObject } from 'react';
import { twMerge } from 'tailwind-merge';
import { storage } from '../Firebase';
import { AnalyticsRange } from '../contexts/DashboardContext';

// Re-export the imported utils
export { camelCaseToTitle, capitalize, chunk, distinct, groupBy };

// Re-export country utilities
export {
  formatCountryLabel,
  getCountryFlag,
  getCountryInfo,
  getCountryName,
} from './country';

export const emptyConverter = <
  T extends DocumentData,
>(): FirestoreDataConverter<T, T> => {
  return {
    toFirestore: (v) => v as any,
    fromFirestore: (v) => v.data() as any,
  };
};

export function classNames(...classes: any[]) {
  return twMerge(...classes.filter(Boolean));
}

export async function uploadFile(file: File, path: string) {
  const res = await uploadBytes(ref(storage, path), file);

  return getDownloadURL(res.ref);
}

// export const PREVIEW_ROOT = 'https://preview_.superfunnel.page';
// export const PREVIEW_ROOT = 'https://superfunnel.page';
const devSitesUrl = import.meta.env.VITE_SITES_URL || 'http://localhost:4000';
export const PREVIEW_ROOT = import.meta.env.DEV
  ? `http://preview_.localhost:${new URL(devSitesUrl).port}`
  : 'https://superfunnel.page';

export function buildPreviewUrl(
  domain: string,
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  websiteId: string,
  token: string,
  editable: boolean,
) {
  return `${PREVIEW_ROOT}?site=${domain}&websiteId=${websiteId}&accountId=${accountId}&campaignId=${dynamicPageId}&variantId=${variantId}&token=${token}${editable ? `&editable=${editable}` : ''}`;
}

export const STEPPER_PREVIEW_URL = `${PREVIEW_ROOT}/stepper`;

export function sendMessageToIframe(
  message: any,
  ref?: RefObject<HTMLIFrameElement | null>,
) {
  const cleanMessage = JSON.stringify(message);

  if (ref?.current) {
    ref.current.contentWindow?.postMessage(cleanMessage, PREVIEW_ROOT);
    return true;
  }

  const iframes = document.getElementsByClassName('iframe-website-preview');

  for (const iframeElem of iframes) {
    (iframeElem as HTMLIFrameElement).contentWindow?.postMessage(
      cleanMessage,
      PREVIEW_ROOT,
    );
  }

  return true;
}

export function sendMessageToIframeByWebsiteId(
  message: any,
  websiteId: string,
) {
  const cleanMessage = JSON.stringify(message);

  const iframes = document.getElementsByClassName('iframe-website-preview');

  for (const iframeElem of iframes) {
    const iframeWebsiteId =
      iframeElem.attributes.getNamedItem('data-website-id')?.value;
    if (!iframeWebsiteId) continue;
    if (iframeWebsiteId !== websiteId) continue;
    (iframeElem as HTMLIFrameElement).contentWindow?.postMessage(
      cleanMessage,
      PREVIEW_ROOT,
    );
  }

  return true;
}

export type WithId<T> = { id: string } & T;

function dateToString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function analyticsRangeToDates(analyticsRange: AnalyticsRange): {
  startDate: string;
  endDate: string;
} {
  if (!analyticsRange) {
    throw new Error('analyticsRange is required');
  }

  if (typeof analyticsRange === 'string') {
    switch (analyticsRange) {
      case 'today': {
        const startDate = new Date();
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'yesterday': {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 1);
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);

        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last7Days': {
        const endDate = new Date();
        const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last30Days': {
        const endDate = new Date();
        const startDate = new Date(
          endDate.getTime() - 30 * 24 * 60 * 60 * 1000,
        );
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'currentMonth': {
        const startDate = new Date();
        startDate.setDate(1);
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      default:
        throw new Error('unsupported date range');
    }
  }

  const startDate = new Date(analyticsRange.startDate || '');
  const endDate = new Date(analyticsRange.endDate || '');

  return {
    startDate: dateToString(startDate),
    endDate: dateToString(endDate),
  };
}

export type AnalyticsAttributes = {
  visitors: number;
  conversions: number;
  allConversions: number;
  buttonClicks: number;
  uniqueButtonClicks: number;
  formSubmissions: number;
  cr: number;
};

export type AnalyticsAttributesWithCustomEvents = AnalyticsAttributes & {
  [key: string]: number;
};

export function getAnalyticsAttributes(
  key: string,
  analyticsObj?: Record<string, any>,
): AnalyticsAttributes {
  const visitors = analyticsObj?.uniqueVisitors?.[key] ?? 0;
  const conversions = analyticsObj?.uniqueConversions?.[key] ?? 0;
  const allConversions = analyticsObj?.uniqueConversions?.['totalCustom'] ?? 0;
  const buttonClicks = analyticsObj?.uniqueButtonClicks?.[key] ?? 0;
  const uniqueButtonClicks =
    analyticsObj?.uniqueButtonClicks?.['uniqueCustom'] ?? 0;
  const formSubmissions = analyticsObj?.uniqueFormSubmissions?.[key] ?? 0;
  const cr = visitors ? Math.ceil((conversions / visitors) * 100) : 0;

  return {
    visitors,
    conversions,
    allConversions,
    buttonClicks,
    uniqueButtonClicks,
    formSubmissions,
    cr,
  };
}

export function getAnalyticsAttributesWithEvents(
  key: string,
  analyticsObj?: Record<string, any>,
  customEvents?: (ExternalPixelEvent | CustomAnalyticsEvent)[],
): AnalyticsAttributesWithCustomEvents {
  const attrs = getAnalyticsAttributes(key, analyticsObj);

  return Object.assign(
    {},
    attrs,
    customEvents?.reduce(
      (acc, event) => {
        acc[event.id] = analyticsObj?.[event.id]?.[key] ?? 0;
        return acc;
      },
      {} as Record<string, number>,
    ),
  );
}

export function getDynamicPageRelevantDomains(
  domains: QueryDocumentSnapshot<Domain>[],
  dynamicPage: DynamicPage,
): QueryDocumentSnapshot<Domain>[] {
  if (dynamicPage.domains) {
    return domains.filter((d) =>
      dynamicPage.domains?.includes(d.data().domain),
    );
  }

  return domains;
}

export function getDynamicPagePrimaryDomain(
  relevantDomains: QueryDocumentSnapshot<Domain>[],
): QueryDocumentSnapshot<Domain> | undefined {
  if (!relevantDomains) return;

  return (
    relevantDomains.find((d) => d.data().type === 'custom') ||
    relevantDomains[0]
  );
}
